import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import type { DocumentItem } from '../types';

function documentName(item: DocumentItem, index: number): string {
  return item.name || item.nume || item.fileNameOriginal || `Document ${index + 1}`;
}

function isImage(item: DocumentItem): boolean {
  const mimeType = String(item.mimeType || '').toLowerCase();
  if (mimeType.startsWith('image/')) return true;
  return /\.(?:avif|bmp|gif|jpe?g|png|webp)$/i.test(documentName(item, 0));
}

function humanSize(value?: number): string {
  const bytes = Math.max(0, Number(value || 0));
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function DocumentImage({ item, index }: { item: DocumentItem; index: number }) {
  const [blobUrl, setBlobUrl] = useState('');
  const [failure, setFailure] = useState('');
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);
  const inFlight = useRef(false);
  const source = item.previewUrl || item.url || '';
  useEffect(() => {
    let active = true;
    let localUrl = '';
    const controller = new AbortController();
    setBlobUrl('');
    setFailure('');
    if (!source) {
      inFlight.current = false;
      setLoading(false);
      setFailure('Imaginea nu are o adresă de încărcare.');
      return undefined;
    }
    inFlight.current = true;
    setLoading(true);
    void api.documentBlob(source, controller.signal).then((blob) => {
      if (!active) return;
      localUrl = URL.createObjectURL(blob);
      setBlobUrl(localUrl);
    }).catch((reason) => {
      if (active) setFailure(reason instanceof Error ? reason.message : 'Imaginea nu poate fi încărcată.');
    }).finally(() => {
      if (active) { inFlight.current = false; setLoading(false); }
    });
    return () => {
      active = false;
      controller.abort();
      if (localUrl) URL.revokeObjectURL(localUrl);
    };
  }, [source, attempt]);
  function retry() {
    // The ref locks immediately, before React renders the disabled button.
    if (inFlight.current || !source) return;
    inFlight.current = true;
    setLoading(true);
    setFailure('');
    setAttempt((value) => value + 1);
  }
  const name = documentName(item, index);
  return <div className="document-image">
    <button type="button" className="document-image-open" disabled={loading || Boolean(failure) || !blobUrl} onClick={() => void download(item, index)} title={`Descarcă ${name}`} aria-label={`Descarcă ${name}`}>
      {failure ? <div className="document-preview-fallback" title={failure}>Imagine indisponibilă</div>
        : blobUrl ? <img src={blobUrl} alt={name} onError={() => setFailure('Fișierul primit nu poate fi afișat ca imagine.')} />
          : <div className="document-preview-fallback">Se încarcă…</div>}
    </button>
    {source && (failure || (attempt > 0 && loading)) && <button type="button" className="document-image-retry" disabled={loading} onClick={retry} aria-label={`Reîncarcă fotografia ${name}`}>{loading ? 'Se reîncarcă…' : 'Reîncearcă'}</button>}
    <span>{name}</span>
  </div>;
}

async function download(item: DocumentItem, index: number): Promise<void> {
  const source = item.downloadUrl || item.url || '';
  if (!source) return;
  const blob = await api.documentBlob(source);
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = documentName(item, index);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1_000);
}

export function DocumentGallery({ documents }: { documents: DocumentItem[] }) {
  if (!documents.length) return null;
  return <div className="document-gallery">
    {documents.map((item, index) => {
      const key = item._id || item.id || `${documentName(item, index)}-${index}`;
      if (isImage(item)) {
        return <DocumentImage item={item} index={index} key={key} />;
      }
      return <button type="button" className="document-file" key={key} onClick={() => void download(item, index)}>
        <span className="document-file-icon">📄</span>
        <span><strong>{documentName(item, index)}</strong><small>{[item.mimeType, humanSize(item.fileSize)].filter(Boolean).join(' · ')}</small></span>
        <span aria-hidden="true">↓</span>
      </button>;
    })}
  </div>;
}
